import { loadOfflineSnapshot, saveOfflineSnapshot } from '@/lib/offline/dataStore';
import type { FetchMenuResponse, MenuCategory, MenuItem } from '@/lib/api/menu';

const NAMESPACE = 'menu';

export interface MenuConflictRecord {
  changedItemCount: number;
  fallbackAgeMs: number;
  previousVersion: string;
  nextVersion: string;
  fallbackAt: number;
}

interface MenuCacheState {
  payload: FetchMenuResponse;
  version: string;
  cachedAt: number;
  lastFallbackAt?: number;
  fallbackVersion?: string;
}

const cloneMenuItem = (item: MenuItem): MenuItem => ({
  ...item,
  modifiers: item.modifiers ? item.modifiers.map((modifier) => ({ ...modifier })) : undefined,
  modifierGroups: item.modifierGroups
    ? item.modifierGroups.map((group) => ({
        ...group,
        modifiers: group.modifiers.map((modifier) => ({ ...modifier })),
      }))
    : undefined,
  tags: item.tags ? [...item.tags] : undefined,
});

const cloneMenuCategory = (category: MenuCategory): MenuCategory => ({
  ...category,
  items: category.items.map(cloneMenuItem),
});

const cloneMenuPayload = (menu: FetchMenuResponse): FetchMenuResponse => ({
  ...menu,
  categories: menu.categories.map(cloneMenuCategory),
});

const simpleHash = (input: string): string => {
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }
  return hash.toString(16);
};

const fingerprintMenu = (menu: FetchMenuResponse): string => {
  const canonicalCategories = menu.categories.map((category) => ({
    id: category.id,
    sortOrder: category.sortOrder,
    isActive: category.isActive !== false,
    availability: category.availability?.status ?? null,
    autoExpireAt: category.autoExpireAt ?? null,
    items: category.items.map((item) => ({
      id: item.id,
      priceCents: item.priceCents,
      isAvailable: item.isAvailable,
      isEightySixed: item.isEightySixed,
      soldOutUntil: item.soldOutUntil ?? null,
      autoExpireAt: item.autoExpireAt ?? null,
      categoryAutoExpireAt: item.categoryAutoExpireAt ?? null,
      availability: item.availability?.status ?? null,
      modifierCount: item.modifiers?.length ?? 0,
      tagSignature: item.tags?.slice().sort().join(',') ?? '',
    })),
  }));
  return simpleHash(
    JSON.stringify({
      timezone: menu.timezone ?? null,
      categoryCount: menu.categories.length,
      canonicalCategories,
    }),
  );
};

const flattenMenu = (menu: FetchMenuResponse): Map<string, MenuItem> => {
  const map = new Map<string, MenuItem>();
  for (const category of menu.categories) {
    for (const item of category.items) {
      map.set(item.id, item);
    }
  }
  return map;
};

const computeChangedItemCount = (previous: FetchMenuResponse, next: FetchMenuResponse): number => {
  const previousItems = flattenMenu(previous);
  const nextItems = flattenMenu(next);
  let changes = 0;

  for (const [id, previousItem] of previousItems.entries()) {
    const current = nextItems.get(id);
    if (!current) {
      changes += 1;
      continue;
    }

    if (
      previousItem.priceCents !== current.priceCents ||
      previousItem.isAvailable !== current.isAvailable ||
      previousItem.isEightySixed !== current.isEightySixed ||
      (previousItem.soldOutUntil ?? null) !== (current.soldOutUntil ?? null) ||
      (previousItem.autoExpireAt ?? null) !== (current.autoExpireAt ?? null) ||
      (previousItem.categoryAutoExpireAt ?? null) !== (current.categoryAutoExpireAt ?? null)
    ) {
      changes += 1;
      continue;
    }

    const previousTags = previousItem.tags?.slice().sort().join(',') ?? '';
    const nextTags = current.tags?.slice().sort().join(',') ?? '';
    if (previousTags !== nextTags) {
      changes += 1;
      continue;
    }

    const previousModifierCount = previousItem.modifiers?.length ?? 0;
    const nextModifierCount = current.modifiers?.length ?? 0;
    if (previousModifierCount !== nextModifierCount) {
      changes += 1;
    }
  }

  for (const id of nextItems.keys()) {
    if (!previousItems.has(id)) {
      changes += 1;
    }
  }

  return changes;
};

const normalizeMenuState = (raw: unknown): MenuCacheState | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const candidate = raw as Partial<MenuCacheState>;

  if (candidate.payload) {
    const payload = cloneMenuPayload(candidate.payload as FetchMenuResponse);
    const version =
      typeof candidate.version === 'string' && candidate.version.length > 0
        ? candidate.version
        : fingerprintMenu(payload);
    const cachedAt = typeof candidate.cachedAt === 'number' ? candidate.cachedAt : Date.now();
    const lastFallbackAt =
      typeof candidate.lastFallbackAt === 'number' ? candidate.lastFallbackAt : undefined;
    const fallbackVersion =
      typeof candidate.fallbackVersion === 'string' ? candidate.fallbackVersion : undefined;
    return { payload, version, cachedAt, lastFallbackAt, fallbackVersion };
  }

  if ('categories' in candidate && Array.isArray((candidate as FetchMenuResponse).categories)) {
    const payload = cloneMenuPayload(candidate as FetchMenuResponse);
    const version = fingerprintMenu(payload);
    const cachedAtCandidate = (candidate as { cachedAt?: number }).cachedAt;
    const cachedAt = typeof cachedAtCandidate === 'number' ? cachedAtCandidate : Date.now();
    return { payload, version, cachedAt };
  }

  return null;
};

const persistMenuState = (tenantSlug: string, state: MenuCacheState): void => {
  saveOfflineSnapshot(NAMESPACE, tenantSlug, state, state.version);
};

export const loadMenuCache = (tenantSlug: string): MenuCacheState | null => {
  const snapshot = loadOfflineSnapshot<MenuCacheState>(NAMESPACE, tenantSlug);
  if (!snapshot) {
    return null;
  }
  return normalizeMenuState(snapshot.data);
};

export const recordMenuFallbackUsage = (tenantSlug: string): MenuCacheState | null => {
  const snapshot = loadMenuCache(tenantSlug);
  if (!snapshot) {
    return null;
  }
  const nextState: MenuCacheState = {
    ...snapshot,
    lastFallbackAt: Date.now(),
    fallbackVersion: snapshot.version,
  };
  persistMenuState(tenantSlug, nextState);
  return nextState;
};

export const mergeNetworkMenuSnapshot = (
  tenantSlug: string,
  menu: FetchMenuResponse,
): { menu: FetchMenuResponse; conflict?: MenuConflictRecord } => {
  const currentFingerprint = fingerprintMenu(menu);
  const previousState = loadMenuCache(tenantSlug);

  let conflict: MenuConflictRecord | undefined;

  if (
    previousState &&
    previousState.lastFallbackAt &&
    previousState.fallbackVersion &&
    previousState.fallbackVersion !== currentFingerprint
  ) {
    const changedItemCount = computeChangedItemCount(previousState.payload, menu);
    conflict = {
      changedItemCount,
      fallbackAgeMs: Date.now() - previousState.lastFallbackAt,
      previousVersion: previousState.fallbackVersion,
      nextVersion: currentFingerprint,
      fallbackAt: previousState.lastFallbackAt,
    };
  }

  const nextState: MenuCacheState = {
    payload: cloneMenuPayload(menu),
    version: currentFingerprint,
    cachedAt: Date.now(),
  };
  persistMenuState(tenantSlug, nextState);

  return { menu: nextState.payload, conflict };
};
